/**
 * Duplicate API Call Tracker
 *
 * This utility tracks API calls and detects duplicates within a time window.
 * It's designed to help identify performance issues caused by redundant API requests.
 */

import { ENV } from "@/config";
import * as Sentry from "@sentry/react";

interface CallRecord {
  url: string;
  method: string;
  params: string; // Serialized params for comparison
  timestamp: number;
  route: string;
  count: number;
}

class DuplicateCallTracker {
  private calls: Map<string, CallRecord> = new Map();
  private currentRoute: string = "";
  private timeWindowMs: number = 10000; // 10 seconds
  private enabled: boolean = true; // Can be disabled in production

  constructor(timeWindowMs: number = 10000) {
    this.timeWindowMs = timeWindowMs;
    // Only enable in development by default
    this.enabled = ENV !== "production" && process.env.NODE_ENV !== "test";
  }

  /**
   * Set the current route - called when route changes
   */
  setRoute(route: string): void {
    if (this.currentRoute !== route) {
      this.currentRoute = route;
      this.clearTracking();
    }
  }

  /**
   * Get current route
   */
  getRoute(): string {
    return this.currentRoute;
  }

  /**
   * Generate a unique key for an API call based on pathname only
   */
  private generateKey(url: string, method: string, _params: unknown): string {
    // Extract pathname only (ignore query params)
    const pathname = url.split("?")[0];
    return `${method}:${pathname}`;
  }

  /**
   * Track an API call and detect duplicates
   */
  trackCall(
    url: string,
    method: string,
    params: unknown = null
  ): { isDuplicate: boolean; count: number } {
    if (!this.enabled) {
      return { isDuplicate: false, count: 1 };
    }

    const now = Date.now();
    const key = this.generateKey(url, method, params);

    // Clean up old entries outside the time window
    this.cleanupOldCalls(now);

    const existingCall = this.calls.get(key);

    if (existingCall && existingCall.route === this.currentRoute) {
      const timeDiff = now - existingCall.timestamp;

      if (timeDiff < this.timeWindowMs) {
        // Duplicate detected!
        existingCall.count++;

        this.logDuplicateWarning(url, method, params, existingCall.count, timeDiff);

        return { isDuplicate: true, count: existingCall.count };
      }
    }

    // Record the new call
    this.calls.set(key, {
      url,
      method,
      params: params ? JSON.stringify(params) : "",
      timestamp: now,
      route: this.currentRoute,
      count: 1,
    });

    return { isDuplicate: false, count: 1 };
  }

  /**
   * Clean up calls that are outside the time window
   */
  private cleanupOldCalls(currentTime: number): void {
    const keysToDelete: string[] = [];

    this.calls.forEach((record, key) => {
      if (currentTime - record.timestamp > this.timeWindowMs) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.calls.delete(key));
  }

  /**
   * Log duplicate call warning
   */
  private logDuplicateWarning(
    url: string,
    method: string,
    params: unknown,
    count: number,
    timeDiff: number
  ): void {
    const timeInSeconds = (timeDiff / 1000).toFixed(2);

    const errorMessage =
      `⚠️ Duplicate API Call Detected (${count}x)\n` +
      `Route: ${this.currentRoute}\n` +
      `Method: ${method}\n` +
      `URL: ${url}\n` +
      `${params ? `Params: ${JSON.stringify(params)}\n` : ""}` +
      `Time since first call: ${timeInSeconds}s\n` +
      `Total duplicate calls: ${count}`;

    console.error(errorMessage);

    // Send to Sentry
    Sentry.captureMessage(errorMessage, {
      level: "warning",
      tags: {
        type: "duplicate_api_call",
        route: this.currentRoute,
        method,
      },
      extra: {
        url,
        params,
        count,
        timeSinceFirstCall: timeInSeconds,
      },
    });
  }

  /**
   * Clear all tracking data (called on route change)
   */
  clearTracking(): void {
    this.calls.clear();
  }

  /**
   * Enable or disable tracking
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearTracking();
    }
  }

  /**
   * Check if tracking is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get statistics about tracked calls
   */
  getStats(): {
    totalCalls: number;
    duplicateCalls: number;
    route: string;
  } {
    let duplicateCalls = 0;
    this.calls.forEach(record => {
      if (record.count > 1) {
        duplicateCalls += record.count - 1;
      }
    });

    return {
      totalCalls: this.calls.size,
      duplicateCalls,
      route: this.currentRoute,
    };
  }
}

// Export singleton instance
export const duplicateCallTracker = new DuplicateCallTracker();

// Export class for testing
export { DuplicateCallTracker };
